/**
 * The wire contract of the synthetic 05 LocalAsset surface. LOCAL SYNTHETIC / NON-OPERATIONAL.
 *
 * <p>WHY EVERY FIELD IS CHECKED AND NONE IS DEFAULTED. A view built from a partially understood
 * response is a view that can show a value the server never sent. The parsers below therefore refuse
 * a body whose shape they do not recognise exactly, and the caller turns that refusal into
 * `CONTRACT_DEFECT` rather than into a business outcome.
 *
 * <p>WHAT IS DELIBERATELY NOT VALIDATED HERE. Whether the asset id is real, whether a revision
 * exists, whether a link is permitted — none of that is this layer's question. The server decides
 * all of it, and a client that pre-checked would be a second, unreviewed authority.
 */
import type {
  AssetReviewCaseView,
  AssetScopeView,
  AssetState,
  AssetView,
  DeclarationInput,
  DeclarationView,
  ExactPublicationView,
  LinkKind,
  ProductLinkInput,
  ProductPublishedRefInput,
  PublicationView,
  PublishedProductView,
  SubmissionView,
} from './local-asset-workflow-port';

export { parseServerError } from '../../../../shared/product-review/adapters/product-review-wire';

const ASSET_STATES: readonly AssetState[] = ['DRAFT', 'SUBMITTED', 'PUBLISHED'];
const LINK_KINDS: readonly LinkKind[] = ['LINKED', 'NOT_LINKED', 'UNKNOWN'];

function object(value: unknown, what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`Invalid ${what}`);
  }
  return value as Record<string, unknown>;
}

function text(source: Record<string, unknown>, field: string): string {
  const value = source[field];
  if (typeof value !== 'string') throw new Error(`Invalid ${field}`);
  return value;
}

function optionalText(source: Record<string, unknown>, field: string): string {
  const value = source[field];
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') throw new Error(`Invalid ${field}`);
  return value;
}

function nullableText(source: Record<string, unknown>, field: string): string | null {
  const value = source[field];
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') throw new Error(`Invalid ${field}`);
  return value;
}

function count(source: Record<string, unknown>, field: string): number {
  const value = source[field];
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 1) {
    throw new Error(`Invalid ${field}`);
  }
  return value;
}

function list(source: Record<string, unknown>, field: string): readonly unknown[] {
  const value = source[field];
  if (!Array.isArray(value)) throw new Error(`Invalid ${field}`);
  return value;
}

/** A single-key envelope, so a response cannot be read as something it is not. */
function envelope(body: unknown, key: string): readonly unknown[] {
  const source = object(body, `${key} envelope`);
  if (Object.keys(source).length !== 1) throw new Error(`Invalid ${key} envelope`);
  return list(source, key);
}

function productRef(value: unknown): ProductPublishedRefInput {
  const source = object(value, 'product reference');
  return {
    productId: text(source, 'productId'),
    country: text(source, 'country'),
    supplier: text(source, 'supplier'),
    publishedVersion: count(source, 'publishedVersion'),
    digest: text(source, 'digest'),
  };
}

export function parseProductLink(value: unknown): ProductLinkInput {
  const source = object(value, 'product link');
  const kind = text(source, 'kind') as LinkKind;
  if (!LINK_KINDS.includes(kind)) throw new Error('Invalid product link kind');
  const product = source['product'];
  if (kind === 'LINKED') {
    if (product === null || product === undefined)
      throw new Error('A linked product link needs a reference');
    return { kind, product: productRef(product) };
  }
  // The server never sends a reference for a non-linked claim, and accepting one here would let the
  // page hold a product fact the equipment does not claim.
  if (product !== null && product !== undefined)
    throw new Error('A non-linked product link carries no reference');
  return { kind };
}

function declaration(value: unknown): DeclarationView {
  const source = object(value, 'declaration');
  return {
    displayName: text(source, 'displayName'),
    category: text(source, 'category'),
    internalRef: optionalText(source, 'internalRef'),
    purpose: text(source, 'purpose'),
    configurationDeclaration: text(source, 'configurationDeclaration'),
    sourceNote: text(source, 'sourceNote'),
    ...(source['contentJson'] === undefined ? {} : { contentJson: text(source, 'contentJson') }),
    productLink: parseProductLink(source['productLink']),
    digest: text(source, 'digest'),
  };
}

function submission(value: unknown): SubmissionView {
  const source = object(value, 'submission');
  return {
    assetId: text(source, 'assetId'),
    country: text(source, 'country'),
    agency: text(source, 'agency'),
    revision: count(source, 'revision'),
    digest: text(source, 'digest'),
    makerAccount: text(source, 'makerAccount'),
    submittedAt: text(source, 'submittedAt'),
  };
}

export function parsePublication(value: unknown): PublicationView {
  const source = object(value, 'publication');
  return {
    assetId: text(source, 'assetId'),
    country: text(source, 'country'),
    agency: text(source, 'agency'),
    publishedVersion: count(source, 'publishedVersion'),
    digest: text(source, 'digest'),
    submissionRevision: count(source, 'submissionRevision'),
    decisionId: text(source, 'decisionId'),
    publisherAccount: text(source, 'publisherAccount'),
    publishedAt: text(source, 'publishedAt'),
  };
}

export function parseAsset(value: unknown): AssetView {
  const source = object(value, 'asset');
  const state = text(source, 'state') as AssetState;
  if (!ASSET_STATES.includes(state)) throw new Error('Invalid asset state');
  return {
    assetId: text(source, 'assetId'),
    country: text(source, 'country'),
    agency: text(source, 'agency'),
    version: count(source, 'version'),
    state,
    declaration: declaration(source['declaration']),
    submissions: list(source, 'submissions').map(submission),
    publications: list(source, 'publications').map(parsePublication),
    returnDecisionId: nullableText(source, 'returnDecisionId'),
    capabilityStatus: text(source, 'capabilityStatus'),
    verificationStatus: text(source, 'verificationStatus'),
  };
}

export function parseAssetList(body: unknown): readonly AssetView[] {
  return envelope(body, 'assets').map(parseAsset);
}

export function parseSubmission(body: unknown): SubmissionView {
  return submission(body);
}

export function parseScope(body: unknown): AssetScopeView {
  const source = object(body, 'scope');
  return {
    country: text(source, 'country'),
    agency: text(source, 'agency'),
    reviewScope: text(source, 'reviewScope'),
  };
}

export function parseProductVersionList(body: unknown): readonly PublishedProductView[] {
  return envelope(body, 'productVersions').map((value) => {
    const source = object(value, 'published product version');
    return {
      productId: text(source, 'productId'),
      country: text(source, 'country'),
      supplier: text(source, 'supplier'),
      publishedVersion: count(source, 'publishedVersion'),
      digest: text(source, 'digest'),
      modelName: text(source, 'modelName'),
      category: text(source, 'category'),
      manufacturerClaims: text(source, 'manufacturerClaims'),
      source: text(source, 'source'),
      publishedAt: text(source, 'publishedAt'),
    };
  });
}

export function parseAssetReviewList(body: unknown): readonly AssetReviewCaseView[] {
  return envelope(body, 'reviewCases').map((value) => {
    const source = object(value, 'review case');
    const decided = source['decision'];
    let decision: AssetReviewCaseView['decision'] = null;
    if (decided !== null && decided !== undefined) {
      const detail = object(decided, 'decision');
      const outcome = text(detail, 'outcome');
      if (outcome !== 'ACCEPTED' && outcome !== 'CHANGES_REQUESTED')
        throw new Error('Invalid outcome');
      decision = {
        decisionId: text(detail, 'decisionId'),
        outcome,
        reason: text(detail, 'reason'),
        checkerAccount: text(detail, 'checkerAccount'),
        decidedAt: text(detail, 'decidedAt'),
      };
    }
    return {
      caseId: text(source, 'caseId'),
      version: count(source, 'version'),
      subjectKind: text(source, 'subjectKind'),
      subjectId: text(source, 'subjectId'),
      revision: count(source, 'revision'),
      digest: text(source, 'digest'),
      sourceEvidence: nullableText(source, 'sourceEvidence'),
      immutableContent: nullableText(source, 'immutableContent'),
      decision,
    };
  });
}

/**
 * The exact-readback envelope. `publication` is present exactly when the status is FOUND, and a body
 * that disagreed with that would be a claim about a version nobody confirmed.
 */
export function parseExactPublication(body: unknown): ExactPublicationView {
  const source = object(body, 'exact publication');
  const status = text(source, 'status');
  if (
    status !== 'FOUND' &&
    status !== 'UNKNOWN' &&
    status !== 'UNAVAILABLE' &&
    status !== 'MISMATCH'
  ) {
    throw new Error('Invalid readback status');
  }
  const publication = source['publication'];
  if (status === 'FOUND') {
    if (publication === null || publication === undefined)
      throw new Error('FOUND without a publication');
    return { status, publication: parsePublication(publication) };
  }
  if (publication !== null && publication !== undefined) {
    throw new Error('a non-FOUND readback carries no publication');
  }
  return { status, publication: null };
}

/** The request body for a declaration. The shape is the server's, and it is built in one place. */
export function declarationBody(declaration: DeclarationInput): Record<string, unknown> {
  const link: Record<string, unknown> = { kind: declaration.productLink.kind };
  if (declaration.productLink.kind === 'LINKED') {
    const product = declaration.productLink.product;
    if (!product) throw new Error('A linked product link needs a reference');
    link['product'] = {
      productId: product.productId,
      country: product.country,
      supplier: product.supplier,
      publishedVersion: product.publishedVersion,
      digest: product.digest,
    };
  }
  return {
    displayName: declaration.displayName,
    category: declaration.category,
    internalRef: declaration.internalRef,
    purpose: declaration.purpose,
    configurationDeclaration: declaration.configurationDeclaration,
    sourceNote: declaration.sourceNote,
    productLink: link,
    ...(declaration.contentJson ? { contentJson: declaration.contentJson } : {}),
  };
}
